import { useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { useN8nVmDelete } from "@/hooks/useResourceQueries";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { N8nVmDeleteDialog } from "./N8nVmDeleteDialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface N8nVmDeletePanelProps {
  resourceId: string;
  resourceName: string;
  vmUuid?: string | null;
}

export function N8nVmDeletePanel({
  resourceId,
  resourceName,
  vmUuid,
}: N8nVmDeletePanelProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const deletion = useN8nVmDelete();

  return (
    <>
      <Card className="border-destructive/30 shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Trash2 className="h-4 w-4 text-destructive" /> Delete VM
          </CardTitle>
          <CardDescription>
            Calls <span className="font-mono">DELETE cloud-vm</span> directly
            through n8n with the UUID recorded for {resourceName}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border bg-muted/30 p-3">
            <p className="text-xs text-muted-foreground">VM UUID</p>
            <code className="break-all text-sm">
              {vmUuid || "No UUID recorded"}
            </code>
          </div>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>This deletes the real VM</AlertTitle>
            <AlertDescription>
              The VM and its data will be removed at the infrastructure.
              WiCloud's resource record, status and billing remain unchanged.
              Handle the resource and billing separately after the provider
              finishes deleting the VM.
            </AlertDescription>
          </Alert>
          {deletion.data && (
            <Alert>
              <AlertTitle>Deletion request accepted</AlertTitle>
              <AlertDescription>
                <p>
                  {deletion.data.providerMessage ||
                    "n8n accepted the deletion request."}
                </p>
                <p className="mt-1 break-all font-mono">
                  UUID: {deletion.data.vmUuid}
                </p>
                {deletion.data.executionId != null && (
                  <p>n8n execution: {deletion.data.executionId}</p>
                )}
                <p className="mt-2">
                  Check VM status to verify completion. WiCloud's record and
                  billing still need to be handled separately.
                </p>
              </AlertDescription>
            </Alert>
          )}
          {deletion.error && (
            <Alert variant="destructive">
              <AlertTitle>Deletion request failed</AlertTitle>
              <AlertDescription>{deletion.error.message}</AlertDescription>
            </Alert>
          )}
          <Button
            variant="destructive"
            disabled={!vmUuid || deletion.isPending || deletion.isSuccess}
            onClick={() => setConfirmOpen(true)}
          >
            {deletion.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            {deletion.isPending ? "Deleting…" : "Delete VM"}
          </Button>
        </CardContent>
      </Card>
      <N8nVmDeleteDialog
        open={confirmOpen}
        resourceName={resourceName}
        vmUuid={vmUuid}
        isPending={deletion.isPending}
        onOpenChange={setConfirmOpen}
        onConfirm={() =>
          deletion.mutate(
            { id: resourceId, name: resourceName, vmUuid },
            { onSettled: () => setConfirmOpen(false) },
          )
        }
      />
    </>
  );
}
